import { router } from "@/crossPlatform/expo-router";
import { encodeNavigationParams } from "@/utils/navigation.util";
import { isWeb } from "@/utils/platform.util";

/**
 * Navigate to a route with automatic parameter encoding
 */
const navigateWithEncoding = ({
  params,
  pathname,
}: {
  params?: Record<string, unknown>;
  pathname: string;
}) => {
  const encodedParams = params ? encodeNavigationParams(params) : undefined;

  router.navigate({
    params: encodedParams,
    pathname,
  });
};

/**
 * Push to a route with automatic parameter encoding
 */
const pushWithEncoding = ({
  params,
  pathname,
}: {
  params?: Record<string, unknown>;
  pathname: string;
}) => {
  const encodedParams = params ? encodeNavigationParams(params) : undefined;

  router.push({
    params: encodedParams,
    pathname,
  });
};

/**
 * Replace current route with automatic parameter encoding
 */
const replaceWithEncoding = ({
  params,
  pathname,
}: {
  params?: Record<string, unknown>;
  pathname: string;
}) => {
  const encodedParams = params ? encodeNavigationParams(params) : undefined;

  router.replace({
    params: encodedParams,
    pathname,
  });
};

// Dismiss modal with optional automatic parameter encoding.
// On web, this will replace the current route with the provided pathname and params.
// On mobile, this will simply dismiss the modal.
const dismissWithEncoding = ({
  params = {},
  pathname,
}: {
  params?: Record<string, unknown>;
  pathname?: string;
}) => {
  const encodedParams = encodeNavigationParams(params);
  if (isWeb() && pathname) {
    router.dismissTo({
      params: encodedParams,
      pathname,
    });
    return;
  }
  router.dismiss();
};

/**
 * Custom hook that provides navigation functions with automatic parameter encoding.
 * This prevents RegExp parsing errors when parameters contain special characters like parentheses.
 *
 * @returns Navigation functions with automatic encoding
 *
 * @example
 * ```tsx
 * const { navigateWithEncoding } = useEncodedNavigation();
 *
 * // Instead of:
 * router.navigate({
 *   params: {
 *     channelDisplayName: encodeNavigationParam(channelDisplayName),
 *     channelUrl: encodeNavigationParam(channelUrl),
 *   },
 *   pathname: ScreenPath.GroupChannel,
 * });
 *
 * // Use:
 * navigateWithEncoding({
 *   params: {
 *     channelDisplayName,
 *     channelUrl,
 *   },
 *   pathname: ScreenPath.GroupChannel,
 * });
 * ```
 */
const useEncodedNavigation = () => {
  return {
    dismissWithEncoding,
    navigateWithEncoding,
    pushWithEncoding,
    replaceWithEncoding,
  };
};

export default useEncodedNavigation;
